import { fallbackPosts, fallbackProjects, fallbackTags } from "../data/fallback-posts";
import type { Post, PostForm, Project, ProjectForm, Tag } from "../types";

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {})
    }
  });

  if (!response.ok) {
    const details = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(details?.error ?? "Request failed.");
  }

  return response.json() as Promise<T>;
}

export async function listPublishedPosts() {
  try {
    return await apiFetch<Post[]>("/api/posts");
  } catch {
    return fallbackPosts;
  }
}

export async function listTags() {
  try {
    return await apiFetch<Tag[]>("/api/tags");
  } catch {
    return fallbackTags;
  }
}

export async function listProjects() {
  try {
    return await apiFetch<Project[]>("/api/projects");
  } catch {
    return fallbackProjects;
  }
}

export async function getPostBySlug(slug: string) {
  try {
    return await apiFetch<Post | null>(`/api/posts/${slug}`);
  } catch {
    return fallbackPosts.find((post) => post.slug === slug) ?? null;
  }
}

export async function savePost(form: PostForm) {
  return apiFetch<{ id: string }>("/api/posts", {
    method: "POST",
    body: JSON.stringify(form)
  });
}

export function listManagedProjects() {
  // Never fall back to local data in the editor: drafts require a real admin session.
  return apiFetch<Project[]>("/api/projects?scope=admin");
}

export function saveProject(form: ProjectForm) {
  return apiFetch<{ id: string }>("/api/projects", {
    method: "POST",
    body: JSON.stringify(form)
  });
}

export function getReadingMinutes(content: string) {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
}

export function listManagedPosts() { return apiFetch<Post[]>("/api/posts?scope=admin"); }
export function listManagedTags() { return apiFetch<Tag[]>("/api/tags"); }
export function saveTag(tag: Omit<Tag, "id"> & { id?: string }) {
  return apiFetch<{ id: string }>("/api/tags", { method: "POST", body: JSON.stringify(tag) });
}
